import type {
  AcfLink,
  BlogCardGridFragment as BlogCardGridQueryResponse,
  Maybe,
} from '@mnd-apps/public/wp-generated/types';
import { useMediaQuery } from '@mnd-frontend/hooks';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import SectionHeader from '@mnd-frontend/ui/public/components/wrappers/SectionHeader';
import {
  BREAKPOINTS,
  SPACING_1,
  SPACING_2,
  SPACING_6,
  SPACING_10,
} from '@mnd-frontend/ui/shared/tokens';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import BlogCard from './BlogCard';
import BlogCardsIndicator from './BlogCardsIndicator';

// #region Helper
type CardKey = keyof Pick<BlogCardGridQueryResponse, 'cardLeft' | 'cardCenter' | 'cardRight'>;
const cardKeys: CardKey[] = ['cardLeft', 'cardCenter', 'cardRight'];

export const BlogCardGridModuleMediaQuery = `@media (min-width: ${BREAKPOINTS.MD}px)`;

export type CardIndexUnion = 0 | 1 | 2;

export type TransformedCardProps = {
  headline: Maybe<string | undefined>;
  description: Maybe<string | undefined>;
  image: { src: Maybe<string | undefined>; alt?: Maybe<string> };
  logo: { src: Maybe<string | undefined>; alt?: Maybe<string> };
  link?: Maybe<AcfLink>;
  kicker?: Maybe<string>;
};

const transformProps = (moduleData: BlogCardGridQueryResponse, cardKey: CardKey) =>
  ({
    kicker: moduleData?.[cardKey]?.kicker,
    headline: moduleData?.[cardKey]?.title,
    description: moduleData?.[cardKey]?.description,
    image: {
      src: moduleData?.[cardKey]?.image?.node.mediaItemUrl,
      alt: moduleData?.[cardKey]?.image?.node.altText,
    },
    logo: {
      src: moduleData?.[cardKey]?.logo?.node.mediaItemUrl,
      alt: moduleData?.[cardKey]?.logo?.node.altText,
    },
    link: moduleData?.[cardKey]?.links,
  }) satisfies TransformedCardProps;
// #endregion

const ModuleContainer = styled.section`
  margin-top: ${SPACING_6};
  margin-bottom: ${SPACING_6};

  @media (min-width: ${BREAKPOINTS.XXL}px) { 
    margin-top: ${SPACING_10};
    margin-bottom: ${SPACING_10};
  }
`;

const GridCardsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    padding: 0 ${SPACING_1}; // Do not use gap here.
    width: 100%;

    @media (min-width: ${BREAKPOINTS.MD}px) { 
        overflow: hidden;
        gap: ${SPACING_2};
    }

    @media (min-width: ${BREAKPOINTS.XXL}px) { 
        padding: 0;
    }
`;

// #region Module Component
const BlogCardGridModule = ({
  moduleData,
}: {
  moduleData: BlogCardGridQueryResponse;
}) => {
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [visibleCardIndex, setVisibleCardIndex] = useState<CardIndexUnion>(0);
  const isLargeDevice = useMediaQuery(BlogCardGridModuleMediaQuery);

  return (
    <ModuleContainer>
      <ContentMaxWidth>
        <SectionHeader
          headline={moduleData.sectionHeadline}
          subHeadline={moduleData.sectionSubheadline}
        />
        <GridCardsContainer ref={cardsContainerRef}>
          {cardKeys.map((cardKey, index) => (
            <BlogCard
              {...(index === 1 ? { ref: cardRef } : {})}
              key={cardKey}
              {...transformProps(moduleData, cardKey)}
              {...(!isLargeDevice ? { hideFromScreenReader: visibleCardIndex !== index } : {})}
            />
          ))}
        </GridCardsContainer>
        <BlogCardsIndicator
          scrollContainerRef={cardsContainerRef}
          targetCardRef={cardRef}
          informSelectedCardIndex={setVisibleCardIndex}
        />
      </ContentMaxWidth>
    </ModuleContainer>
  );
};
// #endregion

export default BlogCardGridModule;
